import { defineStore } from 'pinia';

export const useThemeStore = defineStore('theme', {
  state: () => ({
    currentTheme: 'dark',
    darkModeColors: {
      '--color-background': '#000000',
      '--color-background-menu': '#2A2724',
      '--color-primary-button': '#0F66ABCC',
      '--color-primary-button-border': '#0F66AB',
      '--color-secondary-button': '#6BAB90CC',
      '--color-secondary-button-border': '#6BAB90',
      '--color-green-button': '#2F9E00CC',
      '--color-green-button-border': '#2F9E00',
      '--color-card': '#2A2724',
      '--color-card-border': '#1D1B19',
      '--color-divider': '#1D1B19',
      '--color-modal-color': '#6D5748',
      '--color-modal-divider': '#E0E0E066',
      '--color-background-input': 'rgba(42, 39, 36, 0.05)',
      '--color-background-input-no-opacity': '#4c403a',
      '--color-border-input': 'rgb(69, 64, 60)',
      '--color-background-ai-message': '#909498',
      '--color-background-user-message': '#6b7988',
      '--color-red-button': '#D23838CC',
      '--color-red-button-border': '#CE2626',
      '--text-primary': '#FFFFFF',
      '--text-secondary': '#877f7b',
      '--text-tertiary': '#1e1b1a',
      '--text-muted': '#a0a0a0',
      '--permanent-white': '#FFFFFF',
      '--error-red': '#d23838',
      '--error-red-border': '#CE2626',
      '--icon-red': '',
      '--success-green': '#50a612',
      '--success-green-border': '#49A705',
      '--info-blue': '#10807e',
      '--info-blue-border': '#0F9994',
      '--icon-green': '#009c78',
      '--overlay-color': 'rgba(0, 0, 0, 0.3)',
      '--gradient-top-left': '#252220',
      '--gradient-bottom-right': '#000',
      '--shadow-card': '0 4px 6px rgba(0, 0, 0, 0.3)',
      '--shadow-hover': '0 6px 12px rgba(0, 0, 0, 0.4)',
      '--bg-opacity': '0.25',
      '--header-height': '80px',
      '--footer-height': '80px',
      '--network-health-gradient': 'linear-gradient(135deg, rgba(36, 44, 54, 1) 0%, rgba(34, 47, 41, 1) 100%)',
      '--background-gradient': 'linear-gradient(174.02deg, #252220 0%, #000000 100%)'
    },
    lightModeColors: {
      '--color-background': '#A1A1A1',
      '--color-background-menu': '#ffffff',
      '--color-primary-button': '#0F66ABCC',
      '--color-primary-button-border': '#0F66AB',
      '--color-secondary-button': '#6BAB90CC',
      '--color-secondary-button-border': '#6BAB90',
      '--color-green-button': '#2F9E00CC',
      '--color-green-button-border': '#2F9E00',
      '--color-card': '#ffffff',
      '--color-card-border': '#e0e0e0',
      '--color-divider': '#e0e0e0',
      '--color-modal-color': '#ffffff',
      '--color-modal-divider': '#e0e0e0',
      '--color-background-input': 'rgba(0, 0, 0, 0.05)',
      '--color-background-input-no-opacity': '#f0f0f0',
      '--color-border-input': 'rgb(69, 64, 60)',
      '--color-background-ai-message': '#f0f0f0',
      '--color-background-user-message': '#e8f4f8',
      '--color-red-button': '#D23838CC',
      '--color-red-button-border': '#CE2626',
      '--text-primary': '#1d1d1f',
      '--text-secondary': '#86868b',
      '--text-muted': '#6e6e73',
      '--permanent-white': '#FFFFFF',
      '--error-red': '#d23838',
      '--error-red-border': '#CE2626',
      '--success-green': '#50a612',
      '--success-green-border': '#49A705',
      '--info-blue': '#0F66AB',
      '--info-blue-border': '#0A5490',
      '--icon-green': '#009c78',
      '--overlay-color': 'rgba(0, 0, 0, 0.2)',
      '--gradient-top-left': '#ffffff',
      '--gradient-bottom-right': '#A1A1A1',
      '--shadow-card': '0 2px 8px rgba(0, 0, 0, 0.08)',
      '--shadow-hover': '0 4px 16px rgba(0, 0, 0, 0.12)',
      '--bg-opacity': '0.85',
      '--header-height': '80px',
      '--footer-height': '80px',
      '--network-health-gradient': 'linear-gradient(135deg, rgba(245, 245, 247, 1) 0%, rgba(230, 226, 223, 1) 100%)',
      '--background-gradient': 'linear-gradient(174.02deg, #ffffff 0%, #A1A1A1 100%)'
    }
  }),

  getters: {
    isDarkTheme: (state) => state.currentTheme === 'dark',
    isLightTheme: (state) => state.currentTheme === 'light',
    getCurrentColors: (state) => {
      return state.currentTheme === 'dark' ? state.darkModeColors : state.lightModeColors;
    }
  },

  actions: {
    initTheme() {
      // Default to dark mode
      this.currentTheme = 'dark';
      this.applyTheme();
    },

    toggleTheme() {
      this.currentTheme = this.currentTheme === 'dark' ? 'light' : 'dark';
      this.applyTheme();
      return this.currentTheme;
    },

    setTheme(theme) {
      if (theme === 'dark' || theme === 'light') {
        this.currentTheme = theme;
        this.applyTheme();
      }
    },

    applyTheme() {
      const colors = this.getCurrentColors;
      const root = document.documentElement;
      
      // Apply all color variables to CSS
      Object.entries(colors).forEach(([property, value]) => {
        root.style.setProperty(property, value);
      });

      // Apply light-mode class to body for background image inversion
      if (this.currentTheme === 'light') {
        document.body.classList.add('light-mode');
      } else {
        document.body.classList.remove('light-mode');
      }
    },

    loadThemeFromLoginStore(selectedTheme) {
      if (selectedTheme && (selectedTheme === 'dark' || selectedTheme === 'light')) {
        this.currentTheme = selectedTheme;
      } else {
        this.currentTheme = 'dark'; // Default to dark mode
      }
      this.applyTheme();
    }
  }
});